import { Notification, NotificationInline, OverlayLoader, Tabs } from '@coop/components';
import { useMutation } from '@tanstack/react-query';
import classNames from 'classnames';
import { useEffect, useState } from 'react';

import cartRecipeSelectors from '../../../../cart/selectors/cartRecipeSelectors';
import {
    RecipeProductSubstitutionProvider,
    useRecipeProductSubstitutionContext,
} from '../../../../ecommerce/contexts/RecipeProductSubstitutionContext';
import useRecipeCart from '../../../../recipe/hooks/useRecipeCart';
import type { RecipeCalculationType, RecipePrioritizationType } from '../../../../recipe/models';
import type { ApiAddMultipleRecipesRequest } from '../../../api/recipe/requests/recipeApiRequests';
import { useGroupError } from '../../../hooks/useGroupError';
import { useAppDispatch, useAppSelector } from '../../../hooks/useThunkDispatch';
import { storeSelectors } from '../../../selectors/storeSelectors';
import { ErrorGroup } from '../../../store/structureDefinitions/errorsState';
import { cartThunks } from '../../../thunks/cartThunks';
import FlyInNavigation from '../../atoms/FlyInNavigation';
import FlyInHeader from '../../FlyInHeader/FlyInHeader';
import FocusRestoreProvider from '../../FocusRestoreProvider';
import BuyableRecipeDetails from '../BuyableRecipeDetails/BuyableRecipeDetails';
import { useBuyableRecipeFlyInTracking } from '../BuyableRecipeFlyIn.hooks';
import styles from './BuyableRecipe.module.scss';
import BuyableRecipeCart from './BuyableRecipeCart';
import BuyableRecipeCartSubstitutes from './BuyableRecipeCartSubstitutes';

const ViewPicker = (props: {
    viewType: 'cart' | 'both';
    substitutesView: React.ReactNode;
    tabView: React.ReactNode;
    cartView: React.ReactNode;
}) => {
    const { itemToSubstitute } = useRecipeProductSubstitutionContext();

    if (itemToSubstitute) {
        return props.substitutesView;
    }

    if (props.viewType === 'both') {
        return props.tabView;
    }

    return props.cartView;
};

interface BuyableRecipeProps {
    recipeId: string;
    recipeName: string;
    recipeType: 'recipe' | 'foodbox';
    defaultPortions: number;
    viewType: 'cart' | 'both';
    allowRecipePageRedirect: boolean;
    analyticsListName: string;
    onBuySuccess: (quantityOfProdcutsAdded: number) => void;
    onClose: () => void;
}

type BuyableRecipeTabs = 'recipeTab' | 'cartTab';

const BuyableRecipe = (props: BuyableRecipeProps) => {
    const {
        updateItemQuantity,
        updateSubstituteProduct,
        updatePortions,
        updateCalculation,
        removeItem,
        calculateCart,
        recipeCart,
        recipeCartProducts,
        getProductExceptions,
        isLoading: isLoadingRecipeCart,
        cartError,
    } = useRecipeCart();

    const currentProductionUnit = useAppSelector(storeSelectors.currentProductionUnit);

    const cartRecipesCalculationType = useAppSelector(
        cartRecipeSelectors.selectRecipesCalculationType,
    );

    // initial cart calculation
    useEffect(() => {
        if (!recipeCart) {
            calculateCart({
                calculation: cartRecipesCalculationType?.calculationType || 'waste',
                prioritization: cartRecipesCalculationType?.prioritizationType || null,
                recipes: [{ recipeId: props.recipeId, portions: props.defaultPortions }],
            });
        }
    }, [
        calculateCart,
        recipeCart,
        cartRecipesCalculationType?.calculationType,
        cartRecipesCalculationType?.prioritizationType,
        props.defaultPortions,
        props.recipeId,
    ]);

    const {
        trackBuyRecipe,
        trackCalculationChange,
        trackPortionChange,
        trackSubstituteProduct,
        trackAddToCart,
    } = useBuyableRecipeFlyInTracking();

    const currentRecipePortions = recipeCart?.recipes.find(
        (recipe) => recipe.code === props.recipeId,
    )?.portions;

    const handlePortionChange = (portionQuantity: number) => {
        trackPortionChange(
            props.recipeName,
            props.recipeId,
            currentRecipePortions,
            portionQuantity,
        );
        updatePortions(props.recipeId, portionQuantity);
    };

    const handleCalculationTypeChange = (
        calculationType: RecipeCalculationType,
        prioritization: RecipePrioritizationType | null,
    ) => {
        trackCalculationChange(
            props.recipeName,
            props.recipeId,
            calculationType,
            prioritization,
            props.defaultPortions,
        );
        updateCalculation(calculationType, prioritization);
    };

    const dispatch = useAppDispatch();

    const {
        isPending: isBuyingRecipe,
        error: buyRecipeError,
        mutate: buyRecipe,
    } = useMutation({
        mutationFn: (request: ApiAddMultipleRecipesRequest) =>
            dispatch(cartThunks.buyMultipleRecipes(request)),
        onSuccess: () => {
            const itemsToTrack =
                recipeCart?.items?.filter(
                    (item) => !!item.quantity && item.quantity > 0 && item.purchasable,
                ) || [];

            if (itemsToTrack.length) {
                trackAddToCart(
                    itemsToTrack.map((item, index) => ({
                        quantity: item.quantity!,
                        productId: item.id,
                        index,
                    })),
                    currentProductionUnit,
                    {
                        list: props.analyticsListName,
                        listPosition: 0,
                        recipe_list_id: recipeCart?.recipes?.map((recipe) => recipe.code).join('|'),
                        recipe_list_name: recipeCart?.recipes
                            ?.map((recipe) => recipe.title)
                            .join('|'),
                        recipe_origin: 'recipes',
                    },
                );
            }

            props.onBuySuccess(itemsToTrack.reduce((curr, next) => curr + (next.quantity || 0), 0));
        },
    });

    useGroupError(ErrorGroup.Modal, buyRecipeError);

    const handleBuy = () => {
        const productExceptions = getProductExceptions();

        trackBuyRecipe(props.recipeName, props.recipeId, currentRecipePortions);

        if (recipeCart) {
            buyRecipe({
                calculation: recipeCart.calculation,
                prioritization: recipeCart.prioritization,
                recipes: recipeCart.recipes.map((x) => ({
                    recipeId: x.code,
                    portions: x.portions,
                    group: props.recipeType,
                })),
                products: productExceptions?.map((x) => ({
                    productId: x.id,
                    qty: x.quantity,
                    substituteId: x.substituteIdentifier,
                })),
            });
        }
    };

    const [selectedTab, setSelectedTab] = useState<BuyableRecipeTabs>('recipeTab');
    const [showNotification, setShowNotification] = useState(false);

    return (
        <RecipeProductSubstitutionProvider>
            <FocusRestoreProvider>
                <ViewPicker
                    viewType={props.viewType}
                    substitutesView={
                        <BuyableRecipeCartSubstitutes
                            storeId={currentProductionUnit}
                            items={recipeCart?.items || []}
                            isLoading={isLoadingRecipeCart}
                            onSubstituteChangeClick={(item, product) => {
                                trackSubstituteProduct(
                                    item.name,
                                    props.recipeId,
                                    product.identifier,
                                    product.name,
                                );
                                updateSubstituteProduct(item, product.identifier);
                            }}
                            onSubstituteProductSuccess={() => {
                                setShowNotification(true);
                            }}
                        />
                    }
                    cartView={
                        <div className={classNames(styles.FullHeight)}>
                            <div className={styles.FullHeightContainer}>
                                <OverlayLoader isLoading={isBuyingRecipe || isLoadingRecipeCart} />
                                <FlyInHeader.Root
                                    heading="Lägg till varor"
                                    navigationSlot={<FlyInNavigation onClose={props.onClose} />}
                                    hideBorderBottom
                                />
                                <NotificationInline
                                    variant="critical"
                                    style={{ margin: 'var(--cds-spacing-md)' }}
                                    role="alert"
                                    animated={false}
                                    show={!!cartError}
                                >
                                    <div>Vi kunde inte ladda produkter</div>
                                </NotificationInline>
                                <BuyableRecipeCart
                                    recipeId={props.recipeId}
                                    portions={props.defaultPortions}
                                    recipeCart={recipeCart}
                                    recipeCartProducts={recipeCartProducts || []}
                                    onItemQuantityChange={updateItemQuantity}
                                    onItemRemove={removeItem}
                                    onPortionChange={handlePortionChange}
                                    onCalculationTypeChange={handleCalculationTypeChange}
                                    onBuyClick={handleBuy}
                                />
                            </div>
                        </div>
                    }
                    tabView={
                        <>
                            <FlyInHeader.Root
                                heading={selectedTab === 'cartTab' ? 'Köp varor' : 'Recept'}
                                navigationSlot={<FlyInNavigation onClose={props.onClose} />}
                                hideBorderBottom
                            />
                            <Tabs.Root
                                className={styles.FullHeight}
                                selectedKey={selectedTab}
                                onSelectionChange={(key) =>
                                    setSelectedTab(key as BuyableRecipeTabs)
                                }
                            >
                                <Tabs.List>
                                    <Tabs.Tab id="recipeTab">
                                        {props.recipeType === 'foodbox' ? 'Beskrivning' : 'Recept'}
                                    </Tabs.Tab>
                                    <Tabs.Tab id="cartTab">Varor</Tabs.Tab>
                                </Tabs.List>
                                <Tabs.Panel id="recipeTab" className={styles.FullHeightContainer}>
                                    <BuyableRecipeDetails
                                        allowRecipePageRedirect={props.allowRecipePageRedirect}
                                        recipeId={props.recipeId}
                                        recipeType={props.recipeType}
                                        onBuyIntentClick={() => setSelectedTab('cartTab')}
                                    />
                                </Tabs.Panel>
                                <Tabs.Panel id="cartTab" className={styles.FullHeightContainer}>
                                    <>
                                        <OverlayLoader
                                            isLoading={isBuyingRecipe || isLoadingRecipeCart}
                                        />
                                        <NotificationInline
                                            variant="critical"
                                            style={{ margin: 'var(--cds-spacing-md)' }}
                                            role="alert"
                                            animated={false}
                                            show={!!cartError}
                                        >
                                            <div>Vi kunde inte ladda produkter</div>
                                        </NotificationInline>
                                        <BuyableRecipeCart
                                            recipeId={props.recipeId}
                                            portions={props.defaultPortions}
                                            recipeCart={recipeCart}
                                            recipeCartProducts={recipeCartProducts || []}
                                            onItemQuantityChange={updateItemQuantity}
                                            onItemRemove={removeItem}
                                            onPortionChange={handlePortionChange}
                                            onCalculationTypeChange={handleCalculationTypeChange}
                                            onBuyClick={handleBuy}
                                        />
                                    </>
                                </Tabs.Panel>
                            </Tabs.Root>
                        </>
                    }
                />
                <Notification
                    type="success"
                    show={showNotification}
                    close={() => setShowNotification(false)}
                    message="Varan ersatt!"
                />
            </FocusRestoreProvider>
        </RecipeProductSubstitutionProvider>
    );
};

export default BuyableRecipe;
